/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { useDevProxy } from '@/service/useLiveHost'
import {
    AGENT_API_KEY,
    AGENT_APP_NAME,
    AGENT_LIVE_HOST,
    AGENT_PROXY_PATH,
} from '@/blackbolt/agent/claw/clawConfig'

// Shared HTTP surface for the claw endpoints: the base-URL resolver and the
// MAP auth-header builder. Both the streaming transport (`clawAdapter`) and the
// image-upload utility (`imageUpload`) call these, so they live here rather
// than in either caller — keeps the two consumers on a shared helper without a
// back-edge between them.

/** Resolved lazily so tests can stub `location` before the first fetch. */
export function getAgentBaseUrl(): string {
    return useDevProxy() ? AGENT_PROXY_PATH : AGENT_LIVE_HOST
}

/**
 * Reuse the gqlFetch auth-header pattern (MAP AmznMusic envelope,
 * x-api-key, device id/type). Auth/device modules are dynamically
 * imported so this file stays loadable from ts-jest's stub-only path.
 */
export async function buildAuthHeaders(): Promise<Record<string, string>> {
    const [
        { getAuth, getAuthToken, getAnonSessionId },
        { getDeviceInfo },
        { getDeviceConfig },
        { localSignInPersistence },
    ] = await Promise.all([
        import('@/auth/authRef'),
        import('@/device/device'),
        import('@/device/deviceConfig'),
        import('@/core/signInPersistence'),
    ])

    const { deviceId } = getDeviceInfo()
    const { deviceTypeId } = getDeviceConfig()

    const headers: Record<string, string> = {
        'content-type': 'application/json',
        'x-amzn-device-id': deviceId,
        'x-amzn-device-type': deviceTypeId,
        dnt: '1',
    }

    if (getAuth().isSignedIn()) {
        const oAuthToken = await getAuthToken()
        headers.authorization = `AmznMusic ${btoa(
            JSON.stringify({
                accessToken: oAuthToken,
                deviceId,
                deviceType: deviceTypeId,
            })
        )}`
        headers['x-api-key'] = AGENT_API_KEY

        let accountRefreshToken = ''
        try {
            const stored = await localSignInPersistence.get()
            accountRefreshToken = stored?.refreshToken ?? ''
        } catch {
            /* best-effort — leave empty */
        }
        const actorRefreshToken = ''
        const actorId = ''
        headers['x-map-account-refresh-token'] = accountRefreshToken
        headers['x-map-actor-refresh-token'] = actorRefreshToken
        headers['x-map-actor-id'] = actorId
        headers['x-map-app-name'] = AGENT_APP_NAME

        headers['x-refresh-token'] = accountRefreshToken
        headers['x-profile-id-refresh-token'] = actorRefreshToken
    } else {
        headers['x-api-key'] = AGENT_API_KEY
        headers['music-territory'] = 'US'
        headers['x-amzn-session-id'] = getAnonSessionId()
    }
    return headers
}
